import { addDays, parseISO, format } from "date-fns";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import type { ReportResolver, ReportRow, Grain, ReportContext } from "@/lib/reports/types";
import { budsjettavvikTaskRows, fetchTaskDeviations } from "./budsjettavvik-task-resolver";

interface ProjectDeviationInput {
  projectId: string;
  name: string;
  budsjett: number;
  brukt: number;
}

/**
 * Ren transform — prosjekt-grain. Én rad per prosjekt med solgte vs. brukte
 * timer og avvik i prosent. Avvik = (brukt - budsjett) / budsjett.
 * Drillbar ned til fase/oppgave.
 */
export function budsjettavvikRows(data: ProjectDeviationInput[]): ReportRow[] {
  return data.map((d) => ({
    id: d.projectId,
    label: d.name,
    canDrill: true,
    cells: {
      budsjett: d.budsjett,
      brukt: d.brukt,
      avvik: d.budsjett ? Math.round(((d.brukt - d.budsjett) / d.budsjett) * 100) : 0,
    },
  }));
}

/**
 * Budsjettavvik per prosjekt — SAMME fasit som Bonusmål-målet på dashboardet
 * (`use-bonus-goals.ts`), brutt ned per prosjekt slik at Sum-raden matcher
 * dashboardets aggregat eksakt:
 *
 * - Scope: prosjekter med `project_status='completed'` og `completed_at` i valgt
 *   periode (`ctx.period`). Velg «År» i periode-velgeren for å treffe dashboardets
 *   YTD-tall.
 * - «Budsjett» = solgte timer = `budget_lines.quantity_hours` på interne linjer
 *   (det som faktisk ble solgt i tilbudet — IKKE assignment.hours, som ofte
 *   mangler på eldre/importerte prosjekter).
 * - «Brukt» = sum av alle `time_entries` (duration_seconds/3600) på prosjektet,
 *   ikke string-matchet mot oppgaver.
 *
 * Speiler bevisst bonus-spørringene (ingen paginering) så de to tallene aldri
 * spriker. Endres bonus-beregningen, må denne følge etter.
 */
async function fetchBudsjettavvik(ctx: ReportContext): Promise<ProjectDeviationInput[]> {
  const from = ctx.period.from;
  // Eksklusiv øvre grense = dagen etter `to`, så hele sluttdagen teller med.
  const toExclusive = format(addDays(parseISO(ctx.period.to), 1), "yyyy-MM-dd");

  const projectsRes = await supabase
    .from("projects")
    .select("id, name")
    .eq("project_status", "completed")
    .is("deleted_at", null)
    .gte("completed_at", from)
    .lt("completed_at", toExclusive);
  if (projectsRes.error) throw projectsRes.error;

  const projects = (projectsRes.data ?? []) as Array<{ id: string; name: string }>;
  const projectIds = projects.map((p) => p.id);
  if (projectIds.length === 0) return [];

  // Begge spørringene paginreres: en full årsperiode kan ha >1000 time_entries
  // (1365 for 2026), og en rå .select() kuttes STILLE ved 1000 → underteller
  // brukt-timer og gir feil avvik (jf. VIB-946 + use-bonus-goals). MÅ matche
  // bonus-fasiten som også paginerer.
  const [budgetLineRows, timeEntryRows] = await Promise.all([
    fetchAllPaginated<{ project_id: string; quantity_hours: number | null }>(
      "budget_lines",
      "project_id, quantity_hours",
      (q) => q.in("project_id", projectIds).eq("cost_type", "internal"),
    ),
    fetchAllPaginated<{ project_id: string; duration_seconds: number | null }>(
      "time_entries",
      "project_id, duration_seconds",
      (q) => q.in("project_id", projectIds).is("deleted_at", null),
    ),
  ]);

  const soldByProject = new Map<string, number>();
  for (const bl of budgetLineRows) {
    soldByProject.set(bl.project_id, (soldByProject.get(bl.project_id) ?? 0) + Number(bl.quantity_hours ?? 0));
  }

  const spentByProject = new Map<string, number>();
  for (const te of timeEntryRows) {
    spentByProject.set(te.project_id, (spentByProject.get(te.project_id) ?? 0) + Number(te.duration_seconds ?? 0) / 3600);
  }

  return projects.map((p) => ({
    projectId: p.id,
    name: p.name ?? "",
    budsjett: Math.round((soldByProject.get(p.id) ?? 0) * 10) / 10,
    brukt: Math.round((spentByProject.get(p.id) ?? 0) * 10) / 10,
  }));
}

export const budsjettavvikResolver: ReportResolver = {
  async fetch(level: Grain, ctx: ReportContext): Promise<ReportRow[]> {
    if (level === "prosjekt") {
      return budsjettavvikRows(await fetchBudsjettavvik(ctx));
    }
    if (level === "oppgave") {
      return budsjettavvikTaskRows(await fetchTaskDeviations(ctx));
    }
    return [];
  },
};
